/**
 * Sets up the ui-routing for the app to handle template switches based on the page's current state
 */
import landingPage from '../templates/landing/landing.html';
import menuPage from '../templates/order/menu/menuBody.html';
import checkoutDialogPage from '../templates/checkout/checkoutSteps/checkoutDialog.html';
import checkoutCompletePage from '../templates/checkout/checkoutSteps/checkoutComplete.html';

export function AppConfig($stateProvider, $animateProvider, $urlRouterProvider, $httpProvider, $qProvider, $compileProvider,
    uiMaskConfigProvider, AnalyticsProvider) {
    'ngInject';
    $animateProvider.classNameFilter(/^((?!(geo-loader)).)*$/);
    $compileProvider.preAssignBindingsEnabled(true);
    $qProvider.errorOnUnhandledRejections(false);
    $httpProvider.interceptors.push('httpRequestInterceptor');
    uiMaskConfigProvider.addDefaultPlaceholder = false;

    function resolveAPI(restaurantInfoFactory, ToastApiService, accountService, featureFlagService, GeoIssueReporterService) {
        'ngInject';
        return restaurantInfoFactory.getInfo(['general', 'features', 'restaurantAddress']).then(info => {
            ToastApiService.initialize(info.general.frontendUrl);
            accountService.checkTokens();

            GeoIssueReporterService.setMeta({
                restaurant: {
                    guid: info.general.guid,
                    name: info.general.name,
                    locationName: info.general.locationName,
                    address: info.restaurantAddress
                }
            });
        }).then(() => featureFlagService.getInitialized());
    }

    function resolveGA(googleAnalyticsSetupService, restaurantInfoFactory, AuthenticationService, $injector, $q) {
        'ngInject';

        let trackingIdDfd = googleAnalyticsSetupService.getMetadata()
            .then(data => {
                _configureGA(data);
            });

        let restaurantInfoDfd = restaurantInfoFactory.getInfo(['features', 'general']);

        return $q.all([restaurantInfoDfd, trackingIdDfd]).then(([restaurantInfo]) => {
            _setGACustomDimensions(restaurantInfo, $injector, AuthenticationService);
        });
    }

    function _configureGA({ trackingId }) {
        AnalyticsProvider.setAccount({
            tracker: trackingId,
            trackEvent: true,
            trackEcommerce: true,
            fields: {
                sampleRate: 20 // Disregarding server rendered value
            }
        });
        AnalyticsProvider.useECommerce(true, true);
        AnalyticsProvider.setPageEvent('$stateChangeSuccess');
        AnalyticsProvider.ignoreFirstPageLoad(true);
    }

    function _setGACustomDimensions(restaurantInfo, injector, AuthenticationService) {
        const Analytics = injector.get('Analytics');
        const generalInfo = restaurantInfo.general;

        Analytics.set('appName', process.env.PKG_NAME); // eslint-disable-line no-undef, no-process-env
        Analytics.set('appVersion', process.env.PKG_VERSION); // eslint-disable-line no-undef, no-process-env
        Analytics.set('dimension1', generalInfo.managementGroupName);
        Analytics.set('dimension2', generalInfo.name);
        Analytics.set('dimension3', generalInfo.locationName);
        Analytics.set('dimension4', generalInfo.guid);
        Analytics.set('dimension5', generalInfo.minPosVersionName);
        Analytics.set('dimension6', AuthenticationService.isAuthenticated().toString());
        Analytics.set('dimension7', restaurantInfo.features.join(' '));
    }

    $stateProvider
        .state('start', {
            url: '/',
            views: {
                mainView: {
                    templateUrl: landingPage,
                }
            },
            resolve: { resolveAPI, resolveGA }
        })
        .state('start.login', {
            url: 'login'
        })
        .state('orders', {
            url: '/orders',
            views: {
                mainView: {
                    template: '<orders></orders>'
                },
                headerView: {
                    template: '<toast-header></toast-header><restaurant-info-box></restaurant-info-box>'
                }
            },
            resolve: { resolveAPI, resolveGA }
        })
        .state('profile', {
            url: '/profile',
            views: {
                mainView: {
                    template: '<profile></profile>'
                },
                headerView: {
                    template: '<toast-header></toast-header><restaurant-info-box></restaurant-info-box>'
                }
            },
            resolve: { resolveAPI, resolveGA }
        })
        .state('order', {
            url: '/order',
            views: {
                mainView: {
                    templateUrl: menuPage
                },
                headerView: {
                    template: '<toast-header></toast-header>'
                }
            },
            resolve: { resolveAPI, resolveGA }
        })
        .state('order.spotlight', {
            url: '/feature'
        })
        .state('order.cart', {
            url: '/cart'
        })
        .state('checkout', {
            url: '/checkout',
            views: {
                mainView: {
                    templateUrl: checkoutDialogPage
                },
                headerView: {
                    template: '<toast-header></toast-header><restaurant-info-box></restaurant-info-box>'
                }
            },
            resolve: { resolveAPI, resolveGA }
        })
        .state('complete', {
            url: '/complete',
            views: {
                mainView: {
                    templateUrl: checkoutCompletePage
                },
                headerView: {
                    template: '<toast-header></toast-header><restaurant-info-box></restaurant-info-box>'
                }
            },
            resolve: { resolveAPI, resolveGA }
        });

    $urlRouterProvider
        .when('', '/')
        .otherwise('/order');
}
